import React, { useState } from "react";
import { Trash2, Plus, Pencil, Settings, ChevronDown, ChevronUp, Link, AlertCircle } from "lucide-react";
import type {
  OptionDto,
  MaterialDto,
  CreateOptionAttributeRequest,
  QuantityType,
  PreviewType,
  TriggerType,
  TriggerVisibleType,
} from "../../../../domain/types/company/company-product-system";
import { companyProductSystemUseCase } from "../../../../di/container";
import {
  OPTION_TYPE_LABELS,
  QUANTITY_TYPE_LABELS,
  PREVIEW_TYPE_LABELS,
  SYSTEM_ATTRIBUTE_TYPE_LABELS,
  getAvailableAttributesForOption,
  type PredefinedAttributeTemplate,
} from "./option-constants";
import { OptionAttributeRenderer } from "./OptionAttributeRenderer";
import { resolveErrorMessage } from "../../../../shared/utils/save-flow";
import { ReorderError, SortableItem, SortableList, useInstantReorder } from "./sortable";

interface Props {
  option: OptionDto;
  allOptions: OptionDto[]; // 모든 옵션 (제약 조건 설정용)
  materials: MaterialDto[];
  companyId: number;
  productId: number;
  onDeleteOption: (optionId: number) => void;
  onRefresh: () => Promise<void>;
  /** 옵션 목록에서 순서를 바꾸는 핸들 (ProductOptionList 가 넘긴다) */
  dragHandle?: React.ReactNode;
  /** 조건 옵션보다 앞에 있는 등 순서상 주의할 점 */
  orderWarning?: string;
}

// 모달 컴포넌트
const OptionModal: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[9999] flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="relative bg-white rounded-[16px] shadow-xl p-6 min-w-[450px] max-w-[650px] mx-4 max-h-[90vh] overflow-y-auto text-left">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[16px] font-bold text-[#333]">{title}</h3>
        <button
          onClick={onClose}
          className="w-[28px] h-[28px] flex items-center justify-center rounded-full hover:bg-[#f5f5f5] text-[#999] hover:text-[#555] transition-colors"
        >
          ×
        </button>
      </div>
      {children}
    </div>
  </div>
);

type OptionAttribute = OptionDto["attributes"][number];

/**
 * 쇼핑몰에서 이 항목이 고객에게 비어 보이거나 정리가 필요한 이유.
 *
 * 관리자 화면은 삭제되지 않은 항목을 하나도 거르지 않고 보여 주고,
 * 대신 이 뱃지로 "왜 쇼핑몰에서 이상하게 보이는지"를 알려 정리할 수 있게 한다.
 */
const getAttributeWarnings = (
  attr: OptionAttribute,
  siblings: OptionAttribute[],
): { label: string; title: string }[] => {
  const warnings: { label: string; title: string }[] = [];
  const values = attr.values ?? [];
  if (values.length === 0) {
    warnings.push({ label: "값 없음", title: "선택할 값이 없어 쇼핑몰에서 빈 항목으로 보입니다. 값을 추가하거나 항목을 삭제하세요." });
  } else if (values.every((v) => !v.isEnabled)) {
    warnings.push({ label: "활성 값 없음", title: "모든 값이 비활성화돼 쇼핑몰에서 고를 수 있는 값이 없습니다." });
  }
  const name = attr.name.trim();
  if (siblings.filter((a) => a.name.trim() === name).length > 1) {
    warnings.push({ label: "이름 중복", title: "같은 옵션 안에 이름이 같은 항목이 있어 쇼핑몰에 두 번 보입니다. 하나를 삭제하세요." });
  }
  return warnings;
};

// 트리거 타입 라벨 - 인쇄업자 친화적 설명
const TRIGGER_TYPE_LABELS: Record<string, string> = {
  OR: "선택한 값 중 하나라도 선택하면",
  AND: "선택한 값 모두 선택하면",
  NOR: "선택한 값 중 아무것도 선택 안 하면",
  NAND: "선택한 값 모두 선택 안 하면",
};

const TRIGGER_VISIBLE_TYPE_LABELS: Record<string, string> = {
  HIDE: "숨기기 (아예 안 보임)",
  DISABLE: "비활성화 (보이지만 선택 불가)",
};

export const OptionCard: React.FC<Props> = ({
  option,
  allOptions,
  materials,
  companyId,
  productId,
  onDeleteOption,
  onRefresh,
  dragHandle,
  orderWarning,
}) => {
  // 옵션 수정 상태
  const [showEditOption, setShowEditOption] = useState(false);
  const [editOptionName, setEditOptionName] = useState(option.name);
  const [editOptionDescription, setEditOptionDescription] = useState(option.description || "");
  const [editIsRequired, setEditIsRequired] = useState(option.isRequired);
  const [editIsMultipleAllowed, setEditIsMultipleAllowed] = useState(option.isMultipleAllowed);
  const [editQuantityType, setEditQuantityType] = useState<QuantityType>(option.quantityType);

  // 항목 추가 상태
  const [showAddAttribute, setShowAddAttribute] = useState(false);
  const [newAttrName, setNewAttrName] = useState("");
  const [newAttrDescription, setNewAttrDescription] = useState("");
  const [newAttrPreviewType, setNewAttrPreviewType] = useState<PreviewType | "">("");
  const [newAttrIsMultiSelectable, setNewAttrIsMultiSelectable] = useState(false);
  const [newAttrTriggerValueIds, setNewAttrTriggerValueIds] = useState<number[]>([]);
  const [newAttrTriggerType, setNewAttrTriggerType] = useState<TriggerType>("OR");
  const [newAttrTriggerVisibleType, setNewAttrTriggerVisibleType] = useState<TriggerVisibleType>("HIDE");
  const [showTriggerSettings, setShowTriggerSettings] = useState(false);

  // 항목 수정 상태
  const [editingAttribute, setEditingAttribute] = useState<number | null>(null);
  const [editAttrName, setEditAttrName] = useState("");
  const [editAttrDescription, setEditAttrDescription] = useState("");
  const [editAttrPreviewType, setEditAttrPreviewType] = useState<PreviewType | "">("");
  const [editAttrIsMultiSelectable, setEditAttrIsMultiSelectable] = useState(false);
  const [editAttrIsAddable, setEditAttrIsAddable] = useState(true);
  const [editAttrTriggerValueIds, setEditAttrTriggerValueIds] = useState<number[]>([]);
  const [editAttrTriggerType, setEditAttrTriggerType] = useState<TriggerType>("OR");
  const [editAttrTriggerVisibleType, setEditAttrTriggerVisibleType] = useState<TriggerVisibleType>("HIDE");
  const [showEditTriggerSettings, setShowEditTriggerSettings] = useState(false);

  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // 시스템 옵션 여부
  const isSystemOption = !!option.optionType;

  // 추가 가능한 시스템 속성 목록
  const availableSystemAttributes = getAvailableAttributesForOption(
    option.optionType,
    option.attributes,
  );

  // 모든 옵션의 모든 속성값 목록 (제약 조건 선택용)
  const allAttributeValues = allOptions.flatMap((opt) =>
    opt.attributes
      .filter((attr) => !attr.isDeleted)
      .flatMap((attr) =>
        attr.values.map((val) => ({
          id: val.id,
          optionName: opt.name,
          attributeName: attr.name,
          valueName: val.value || val.type || `값 ${val.id}`,
        }))
      )
  );

  // 옵션 수정
  const handleUpdateOption = async () => {
    setSaving(true);
    try {
      await companyProductSystemUseCase.updateOption(
        companyId,
        productId,
        option.id,
        {
          name: editOptionName,
          description: editOptionDescription || undefined,
          isRequired: editIsRequired,
          isMultipleAllowed: editIsMultipleAllowed,
          quantityType: editQuantityType,
        }
      );
      await onRefresh();
      setShowEditOption(false);
    } catch {
      alert("옵션 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 시스템 속성 추가
  const handleAddSystemAttribute = async (template: PredefinedAttributeTemplate) => {
    setSaving(true);
    try {
      const request: CreateOptionAttributeRequest = {
        name: template.name,
        systemAttributeType: template.systemAttributeType as any,
      };
      await companyProductSystemUseCase.createOptionAttribute(
        companyId,
        productId,
        option.id,
        request,
      );
      await onRefresh();
      setShowAddAttribute(false);
    } catch {
      alert("항목 추가에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 커스텀 속성 추가
  const handleAddCustomAttribute = async () => {
    if (!newAttrName.trim()) return;
    setSaving(true);
    try {
      const request: CreateOptionAttributeRequest = {
        name: newAttrName,
        description: newAttrDescription || undefined,
        previewType: newAttrPreviewType || undefined,
        isMultiSelectable: newAttrIsMultiSelectable,
        isAddable: true,
        triggerAttributeValueIds: newAttrTriggerValueIds.length > 0 ? newAttrTriggerValueIds : undefined,
        triggerType: newAttrTriggerValueIds.length > 0 ? newAttrTriggerType : undefined,
        triggerVisibleType: newAttrTriggerValueIds.length > 0 ? newAttrTriggerVisibleType : undefined,
      };
      await companyProductSystemUseCase.createOptionAttribute(
        companyId,
        productId,
        option.id,
        request,
      );
      await onRefresh();
      closeAddAttributeModal();
    } catch {
      alert("항목 추가에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const closeAddAttributeModal = () => {
    setShowAddAttribute(false);
    setNewAttrName("");
    setNewAttrDescription("");
    setNewAttrPreviewType("");
    setNewAttrIsMultiSelectable(false);
    setNewAttrTriggerValueIds([]);
    setNewAttrTriggerType("OR");
    setNewAttrTriggerVisibleType("HIDE");
    setShowTriggerSettings(false);
  };

  // 항목 수정
  const handleUpdateAttribute = async (attributeId: number) => {
    setSaving(true);
    try {
      await companyProductSystemUseCase.updateOptionAttribute(
        companyId,
        productId,
        option.id,
        attributeId,
        {
          name: editAttrName,
          description: editAttrDescription || undefined,
          previewType: editAttrPreviewType || undefined,
          isMultiSelectable: editAttrIsMultiSelectable,
          isAddable: editAttrIsAddable,
          triggerAttributeValueIds: editAttrTriggerValueIds.length > 0 ? editAttrTriggerValueIds : null,
          triggerType: editAttrTriggerValueIds.length > 0 ? editAttrTriggerType : null,
          triggerVisibleType: editAttrTriggerValueIds.length > 0 ? editAttrTriggerVisibleType : null,
        }
      );
      await onRefresh();
      setEditingAttribute(null);
    } catch {
      alert("항목 수정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // 항목 삭제
  const handleDeleteAttribute = async (attributeId: number) => {
    if (!window.confirm("이 항목을 삭제하시겠습니까?")) return;
    try {
      await companyProductSystemUseCase.deleteOptionAttribute(
        companyId,
        productId,
        option.id,
        attributeId,
      );
      await onRefresh();
    } catch (error) {
      // 서버가 거절한 이유(예: 삭제할 수 없는 시스템 항목)를 그대로 보여 준다
      alert(resolveErrorMessage(error, "항목 삭제에 실패했습니다."));
    }
  };

  // 항목 수정 모달 열기
  const openEditAttribute = (attr: typeof option.attributes[0]) => {
    setEditAttrName(attr.name);
    setEditAttrDescription(attr.description || "");
    setEditAttrPreviewType(attr.previewType || "");
    setEditAttrIsMultiSelectable(attr.isMultiSelectable);
    setEditAttrIsAddable(attr.isAddable);
    setEditAttrTriggerValueIds(attr.triggerAttributeValueIds || []);
    setEditAttrTriggerType(attr.triggerType || "OR");
    setEditAttrTriggerVisibleType(attr.triggerVisibleType || "HIDE");
    setShowEditTriggerSettings((attr.triggerAttributeValueIds?.length || 0) > 0);
    setEditingAttribute(attr.id);
  };

  // 값 목록이 비어 null 로 오는 항목도 화면이 깨지지 않도록 빈 배열로 맞춘다
  const activeAttributes = (option.attributes ?? [])
    .filter((a) => !a.isDeleted)
    .map((a) => (a.values ? a : { ...a, values: [] }));

  // 항목 순서: 이 옵션 안에서만 바꿀 수 있다
  const attributeReorder = useInstantReorder(
    activeAttributes.map((a) => a.id),
    (orderedIds) => companyProductSystemUseCase.reorderOptionItems(
      companyId, productId, { level: "attribute", optionId: option.id }, orderedIds,
    ),
  );
  const attributeById = new Map(activeAttributes.map((a) => [a.id, a]));
  const orderedAttributes = attributeReorder.ids
    .map((id) => attributeById.get(id))
    .filter((a): a is (typeof activeAttributes)[number] => !!a);

  const inputStyle = "w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] text-[#333] placeholder-[#bbb] focus:border-[#4285F4] focus:outline-none transition-colors";
  const selectStyle = "w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2.5 text-[14px] text-[#333] focus:border-[#4285F4] focus:outline-none transition-colors bg-white";

  // 트리거 값 선택 UI 렌더링
  const renderTriggerSelector = (
    selectedIds: number[],
    setSelectedIds: (ids: number[]) => void,
    triggerType: TriggerType,
    setTriggerType: (type: TriggerType) => void,
    triggerVisibleType: TriggerVisibleType,
    setTriggerVisibleType: (type: TriggerVisibleType) => void,
  ) => (
    <div className="p-4 border border-[#e0e0e0] rounded-[10px] bg-[#fafafa]">
      <div className="mb-4">
        <label className="block text-[13px] font-medium text-[#555] mb-1">
          어떤 값이 선택되었을 때 이 항목을 보여줄까요?
        </label>
        <p className="text-[11px] text-[#888] mb-2">
          아래에서 조건이 될 값들을 선택하세요
        </p>
        <div className="max-h-[200px] overflow-y-auto border border-[#e0e0e0] rounded-[8px] bg-white">
          {allAttributeValues.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-[13px] text-[#888]">아직 선택 가능한 값이 없습니다</p>
              <p className="text-[11px] text-[#aaa] mt-1">먼저 다른 항목에 값을 추가해주세요</p>
            </div>
          ) : (
            allAttributeValues.map((item) => (
              <label
                key={item.id}
                className="flex items-center gap-2 px-3 py-2.5 hover:bg-[#f5f5f5] cursor-pointer border-b border-[#f0f0f0] last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds([...selectedIds, item.id]);
                    } else {
                      setSelectedIds(selectedIds.filter((id) => id !== item.id));
                    }
                  }}
                  className="w-4 h-4 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[13px] text-[#333] font-medium">{item.valueName}</span>
                  <span className="text-[11px] text-[#888] ml-2">
                    {item.optionName} → {item.attributeName}
                  </span>
                </div>
              </label>
            ))
          )}
        </div>
        {selectedIds.length > 0 && (
          <p className="text-[12px] text-[#4285F4] mt-2 font-medium">
            ✓ {selectedIds.length}개 조건 선택됨
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-[13px] font-medium text-[#555] mb-2">표시 조건</label>
          <select
            className={selectStyle}
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value as TriggerType)}
          >
            {Object.entries(TRIGGER_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <p className="text-[10px] text-[#999] mt-1">이 항목을 표시할 조건입니다</p>
        </div>
        <div>
          <label className="block text-[13px] font-medium text-[#555] mb-2">조건 미충족 시</label>
          <select
            className={selectStyle}
            value={triggerVisibleType}
            onChange={(e) => setTriggerVisibleType(e.target.value as TriggerVisibleType)}
          >
            {Object.entries(TRIGGER_VISIBLE_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <p className="text-[10px] text-[#999] mt-1">조건이 맞지 않을 때 처리 방식</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="border border-[#e8e8e8] rounded-[12px] overflow-hidden">
      {/* Option header */}
      <div
        className="flex items-center gap-2 px-4 py-3 bg-[#fafafa] cursor-pointer hover:bg-[#f5f5f5] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {dragHandle}
        <button className="text-[#888]">
          {expanded ? <ChevronUp className="w-[16px] h-[16px]" /> : <ChevronDown className="w-[16px] h-[16px]" />}
        </button>
        <h3 className="text-[15px] font-bold text-[#222] flex-1">{option.name}</h3>

        {/* 옵션 타입 태그 */}
        {option.optionType && (
          <span className="px-2 py-0.5 bg-[#E8EFFF] text-[#4285F4] text-[11px] font-medium rounded-full">
            {OPTION_TYPE_LABELS[option.optionType] || option.optionType}
          </span>
        )}

        {/* 설정 아이콘들 */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* 수정 버튼 (시스템 옵션이 아닌 경우) */}
          {!isSystemOption && (
            <button
              onClick={() => {
                setEditOptionName(option.name);
                setEditOptionDescription(option.description || "");
                setEditIsRequired(option.isRequired);
                setEditIsMultipleAllowed(option.isMultipleAllowed);
                setEditQuantityType(option.quantityType);
                setShowEditOption(true);
              }}
              className="p-1.5 text-[#bbb] hover:text-[#4285F4] transition-colors"
              title="옵션 수정"
            >
              <Settings className="w-[16px] h-[16px]" />
            </button>
          )}

          {/* 삭제 버튼 */}
          {option.isDeletable && (
            <button
              onClick={() => onDeleteOption(option.id)}
              className="p-1.5 text-[#bbb] hover:text-[#F24A4A] transition-colors"
              title="옵션 삭제"
            >
              <Trash2 className="w-[16px] h-[16px]" />
            </button>
          )}
        </div>
      </div>

      {orderWarning && (
        <div className="px-4 py-2 bg-[#EBF3FF] border-b border-[#4285F4]/20 text-[12px] text-[#4285F4] flex items-center gap-1.5 text-left">
          <AlertCircle className="w-[13px] h-[13px] flex-shrink-0" />
          {orderWarning}
        </div>
      )}

      {/* Attributes */}
      {expanded && (
        <div className="p-4">
          <ReorderError message={attributeReorder.error} onClose={attributeReorder.clearError} />
          {activeAttributes.length === 0 ? (
            <p className="text-[13px] text-[#bbb] text-center py-4">항목이 없습니다</p>
          ) : (
            <SortableList scope={`attribute-${option.id}`} ids={attributeReorder.ids} onMove={attributeReorder.move}>
            {orderedAttributes.map((attr) => (
              <SortableItem key={attr.id} scope={`attribute-${option.id}`} id={attr.id} label={`'${attr.name}' 항목 순서 이동`} handleSize="sm">
              {({ setNodeRef, style, handle }) => (
              <div ref={setNodeRef} style={style} className="mb-5 last:mb-0 bg-white">
                {/* 항목 헤더 */}
                <div className="flex items-center gap-2 mb-2">
                  {activeAttributes.length > 1 && handle}
                  <span className="text-[13px] font-medium text-[#555]">{attr.name}</span>

                  {/* 시스템 속성 태그 */}
                  {attr.systemAttributeType && (
                    <span className="px-1.5 py-0.5 bg-[#f5f5f5] text-[#888] text-[10px] rounded">
                      {SYSTEM_ATTRIBUTE_TYPE_LABELS[attr.systemAttributeType] || attr.systemAttributeType}
                    </span>
                  )}

                  {/* 다중선택 태그 */}
                  {attr.isMultiSelectable && (
                    <span className="px-1.5 py-0.5 bg-[#EDE7F6] text-[#7B1FA2] text-[10px] rounded">
                      다중선택
                    </span>
                  )}

                  {/* 제약 조건 태그 */}
                  {attr.triggerAttributeValueIds && attr.triggerAttributeValueIds.length > 0 && (
                    <span
                      className="px-1.5 py-0.5 bg-[#FFF3E0] text-[#EF6C00] text-[10px] rounded flex items-center gap-0.5"
                      title="쇼핑몰에서는 조건이 되는 값을 골랐을 때만 보입니다"
                    >
                      <Link className="w-[10px] h-[10px]" />
                      조건부
                    </span>
                  )}

                  {/* 쇼핑몰에서 비어 보이거나 중복된 항목 안내 */}
                  {getAttributeWarnings(attr, activeAttributes).map((w) => (
                    <span
                      key={w.label}
                      className="px-1.5 py-0.5 bg-[#f5f5f5] text-[#888] text-[10px] rounded cursor-help"
                      title={w.title}
                    >
                      {w.label}
                    </span>
                  ))}

                  <div className="flex-1" />

                  {/* 항목 수정 버튼 (시스템 속성이 아닌 경우) */}
                  {!attr.systemAttributeType && (
                    <button
                      onClick={() => openEditAttribute(attr)}
                      className="p-1 text-[#ccc] hover:text-[#4285F4] transition-colors"
                      title="항목 수정"
                    >
                      <Pencil className="w-[14px] h-[14px]" />
                    </button>
                  )}

                  {/* 항목 삭제 버튼.
                      시스템 항목은 서버가 삭제를 막으므로 누를 수 없게 두되, 정리 대상(값 없음·중복)이면
                      왜 지울 수 없는지 알 수 있도록 흐린 아이콘을 남긴다 */}
                  {attr.isDeletable ? (
                    <button
                      onClick={() => handleDeleteAttribute(attr.id)}
                      className="p-1 text-[#ccc] hover:text-[#F24A4A] transition-colors"
                      title="항목 삭제"
                    >
                      <Trash2 className="w-[14px] h-[14px]" />
                    </button>
                  ) : getAttributeWarnings(attr, activeAttributes).length > 0 && (
                    <span
                      className="p-1 text-[#e0e0e0] cursor-not-allowed"
                      title="시스템이 만든 항목이라 여기서 삭제할 수 없습니다"
                    >
                      <Trash2 className="w-[14px] h-[14px]" />
                    </span>
                  )}
                </div>

                {/* 값 렌더러 */}
                <OptionAttributeRenderer
                  attribute={attr}
                  option={option}
                  materials={materials}
                  companyId={companyId}
                  productId={productId}
                  onRefresh={onRefresh}
                />
              </div>
              )}
              </SortableItem>
            ))}
            </SortableList>
          )}

          {/* 항목 추가 버튼 */}
          <button
            onClick={() => setShowAddAttribute(true)}
            className="w-full mt-4 py-2.5 border-2 border-dashed border-[#e0e0e0] rounded-[10px] text-[13px] text-[#888] hover:border-[#4285F4] hover:text-[#4285F4] hover:bg-[#fafafa] transition-colors flex items-center justify-center gap-1"
          >
            <Plus className="w-[14px] h-[14px]" />
            항목 추가
          </button>
        </div>
      )}

      {/* ===== 옵션 수정 모달 ===== */}
      {showEditOption && (
        <OptionModal title="옵션 설정" onClose={() => setShowEditOption(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">옵션 이름 *</label>
              <input
                type="text"
                className={inputStyle}
                value={editOptionName}
                onChange={(e) => setEditOptionName(e.target.value)}
                placeholder="예: 코팅, 후가공, 포장 방법"
              />
              <p className="text-[11px] text-[#999] mt-1">고객에게 보여지는 옵션 이름입니다</p>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">안내 문구 (선택)</label>
              <input
                type="text"
                className={inputStyle}
                value={editOptionDescription}
                onChange={(e) => setEditOptionDescription(e.target.value)}
                placeholder="예: 광택 여부를 선택해주세요"
              />
              <p className="text-[11px] text-[#999] mt-1">고객이 옵션 선택 시 참고할 수 있는 설명입니다</p>
            </div>

            <div className="p-4 bg-[#fafafa] rounded-[10px]">
              <p className="text-[12px] font-medium text-[#666] mb-3">주문 설정</p>

              <div className="space-y-3 w-full">
                <label className="flex items-start justify-start gap-3 cursor-pointer w-full">
                  <input
                    type="checkbox"
                    checked={editIsRequired}
                    onChange={(e) => setEditIsRequired(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px] text-[#333] font-medium block">필수 선택</span>
                    <p className="text-[11px] text-[#888] mt-0.5">체크하면 고객이 반드시 이 옵션을 선택해야 주문할 수 있습니다</p>
                  </div>
                </label>

                <label className="flex items-start justify-start gap-3 cursor-pointer w-full">
                  <input
                    type="checkbox"
                    checked={editIsMultipleAllowed}
                    onChange={(e) => setEditIsMultipleAllowed(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px] text-[#333] font-medium block">같은 옵션 여러 개 추가 가능</span>
                    <p className="text-[11px] text-[#888] mt-0.5">예: 코팅을 앞면, 뒷면에 각각 다르게 적용하고 싶을 때</p>
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">수량 계산 방식</label>
              <select
                className={selectStyle}
                value={editQuantityType}
                onChange={(e) => setEditQuantityType(e.target.value as QuantityType)}
              >
                {Object.entries(QUANTITY_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <p className="text-[11px] text-[#999] mt-2">
                {editQuantityType === "SINGLE" && "옵션 수량이 항상 1개입니다 (예: 코팅 적용 여부)"}
                {editQuantityType === "SELECTABLE" && "고객이 옵션 수량을 직접 선택합니다 (예: 스티커 매수)"}
                {editQuantityType === "MULTIPLIED_BY_PRODUCT_QUANTITY" && (
                  <>
                    고객이 선택한 옵션 수량 × 주문 수량으로 계산됩니다
                    <br />
                    <span className="text-[#4285F4]">예: 간지 3장 선택, 100부 주문 → 간지 총 300장</span>
                  </>
                )}
                {editQuantityType === "SAME_AS_PRODUCT_QUANTITY" && "주문 수량과 동일하게 적용됩니다 (예: 100부 주문 → 옵션도 100개)"}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => setShowEditOption(false)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleUpdateOption}
                disabled={!editOptionName.trim() || saving}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
              >
                저장
              </button>
            </div>
          </div>
        </OptionModal>
      )}

      {/* ===== 항목 추가 모달 ===== */}
      {showAddAttribute && (
        <OptionModal title="선택 항목 추가" onClose={closeAddAttributeModal}>
          <div className="space-y-4">
            {/* 시스템 속성 빠른 추가 (해당되는 경우) */}
            {availableSystemAttributes.length > 0 && (
              <div className="mb-4">
                <div className="p-3 bg-[#E8EFFF] rounded-[8px] mb-3">
                  <p className="text-[12px] font-medium text-[#4285F4] mb-2">💡 자주 쓰는 항목 바로 추가</p>
                  <div className="flex flex-wrap gap-2">
                    {availableSystemAttributes.map((tmpl) => (
                      <button
                        key={tmpl.key}
                        onClick={() => handleAddSystemAttribute(tmpl)}
                        disabled={saving}
                        className="px-3 py-1.5 bg-white border border-[#4285F4] rounded-[6px] text-[12px] text-[#4285F4] hover:bg-[#4285F4] hover:text-white transition-colors disabled:opacity-50"
                      >
                        + {tmpl.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-[#e8e8e8]" />
                  <span className="text-[11px] text-[#999]">또는 직접 만들기</span>
                  <div className="flex-1 h-px bg-[#e8e8e8]" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">항목 이름 *</label>
              <input
                type="text"
                className={inputStyle}
                value={newAttrName}
                onChange={(e) => setNewAttrName(e.target.value)}
                placeholder="예: 색상, 크기, 종류"
                autoFocus
              />
              <p className="text-[11px] text-[#999] mt-1">고객이 선택하게 될 항목 이름입니다</p>
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">안내 문구 (선택)</label>
              <input
                type="text"
                className={inputStyle}
                value={newAttrDescription}
                onChange={(e) => setNewAttrDescription(e.target.value)}
                placeholder="예: 원하시는 색상을 선택해주세요"
              />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">선택 방식</label>
              <select
                className={selectStyle}
                value={newAttrPreviewType}
                onChange={(e) => setNewAttrPreviewType(e.target.value as PreviewType | "")}
              >
                <option value="">기본 (선택 박스)</option>
                <option value="SELECT_BOX">선택 박스 - 여러 개 중 하나 선택</option>
                <option value="CHECK_BOX">체크 박스 - 여러 개 선택 가능</option>
                <option value="DROP_DOWN">드롭다운 - 목록에서 선택</option>
                <option value="IMAGE_SELECT_BOX">이미지 선택 - 사진으로 보고 선택</option>
                <option value="INPUT_NUMBER">숫자 직접 입력</option>
                <option value="INPUT_STRING">텍스트 직접 입력</option>
                <option value="INPUT_DATE_TIME">날짜/시간 선택</option>
              </select>
              <p className="text-[11px] text-[#999] mt-1">고객이 값을 선택하는 방식입니다</p>
            </div>

            <div className="p-3 bg-[#fafafa] rounded-[8px]">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newAttrIsMultiSelectable}
                  onChange={(e) => setNewAttrIsMultiSelectable(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                />
                <div>
                  <span className="text-[13px] text-[#333] font-medium">여러 개 동시 선택 허용</span>
                  <p className="text-[11px] text-[#888]">예: 색상을 빨강 + 파랑 두 가지 선택하고 싶을 때</p>
                </div>
              </label>
            </div>

            {/* 제약 조건 설정 */}
            <div className="border-t border-[#e8e8e8] pt-4">
              <button
                type="button"
                onClick={() => setShowTriggerSettings(!showTriggerSettings)}
                className="flex items-center gap-2 text-[13px] text-[#666] hover:text-[#4285F4]"
              >
                <Link className="w-[14px] h-[14px]" />
                <span className="font-medium">조건부 표시 설정</span>
                <span className="text-[#999]">{showTriggerSettings ? "(접기)" : "(고급)"}</span>
              </button>
              <p className="text-[11px] text-[#999] mt-1">
                다른 항목의 특정 값이 선택되었을 때만 이 항목을 표시합니다
              </p>
              <p className="text-[11px] text-[#888] mt-0.5">
                예: "제본방식"에서 "스프링"을 선택한 경우에만 "스프링 색상" 항목 표시
              </p>

              {showTriggerSettings && (
                <div className="mt-3">
                  {renderTriggerSelector(
                    newAttrTriggerValueIds,
                    setNewAttrTriggerValueIds,
                    newAttrTriggerType,
                    setNewAttrTriggerType,
                    newAttrTriggerVisibleType,
                    setNewAttrTriggerVisibleType,
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={closeAddAttributeModal}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleAddCustomAttribute}
                disabled={!newAttrName.trim() || saving}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
              >
                항목 추가
              </button>
            </div>
          </div>
        </OptionModal>
      )}

      {/* ===== 항목 수정 모달 ===== */}
      {editingAttribute !== null && (
        <OptionModal title="항목 설정" onClose={() => setEditingAttribute(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">항목 이름 *</label>
              <input
                type="text"
                className={inputStyle}
                value={editAttrName}
                onChange={(e) => setEditAttrName(e.target.value)}
                placeholder="예: 색상, 크기, 종류"
              />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">안내 문구 (선택)</label>
              <input
                type="text"
                className={inputStyle}
                value={editAttrDescription}
                onChange={(e) => setEditAttrDescription(e.target.value)}
                placeholder="예: 원하시는 색상을 선택해주세요"
              />
            </div>

            <div>
              <label className="block text-[13px] font-medium text-[#555] mb-2">선택 방식</label>
              <select
                className={selectStyle}
                value={editAttrPreviewType}
                onChange={(e) => setEditAttrPreviewType(e.target.value as PreviewType | "")}
              >
                <option value="">기본 (선택 박스)</option>
                <option value="SELECT_BOX">선택 박스 - 여러 개 중 하나 선택</option>
                <option value="CHECK_BOX">체크 박스 - 여러 개 선택 가능</option>
                <option value="DROP_DOWN">드롭다운 - 목록에서 선택</option>
                <option value="IMAGE_SELECT_BOX">이미지 선택 - 사진으로 보고 선택</option>
                <option value="INPUT_NUMBER">숫자 직접 입력</option>
                <option value="INPUT_STRING">텍스트 직접 입력</option>
                <option value="INPUT_DATE_TIME">날짜/시간 선택</option>
              </select>
            </div>

            <div className="p-3 bg-[#fafafa] rounded-[8px] space-y-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editAttrIsMultiSelectable}
                  onChange={(e) => setEditAttrIsMultiSelectable(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                />
                <div>
                  <span className="text-[13px] text-[#333] font-medium">여러 개 동시 선택 허용</span>
                  <p className="text-[11px] text-[#888]">고객이 값을 2개 이상 선택할 수 있습니다</p>
                </div>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editAttrIsAddable}
                  onChange={(e) => setEditAttrIsAddable(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-[#d0d0d0] text-[#4285F4] focus:ring-[#4285F4]"
                />
                <div>
                  <span className="text-[13px] text-[#333] font-medium">새 값 추가 허용</span>
                  <p className="text-[11px] text-[#888]">이 항목에 새로운 선택 값을 추가할 수 있습니다</p>
                </div>
              </label>
            </div>

            {/* 제약 조건 설정 */}
            <div className="border-t border-[#e8e8e8] pt-4">
              <button
                type="button"
                onClick={() => setShowEditTriggerSettings(!showEditTriggerSettings)}
                className="flex items-center gap-2 text-[13px] text-[#666] hover:text-[#4285F4]"
              >
                <Link className="w-[14px] h-[14px]" />
                <span className="font-medium">조건부 표시 설정</span>
                <span className="text-[#999]">{showEditTriggerSettings ? "(접기)" : "(고급)"}</span>
              </button>
              <p className="text-[11px] text-[#999] mt-1">
                다른 항목의 특정 값이 선택되었을 때만 이 항목을 표시합니다
              </p>

              {showEditTriggerSettings && (
                <div className="mt-3">
                  {renderTriggerSelector(
                    editAttrTriggerValueIds,
                    setEditAttrTriggerValueIds,
                    editAttrTriggerType,
                    setEditAttrTriggerType,
                    editAttrTriggerVisibleType,
                    setEditAttrTriggerVisibleType,
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => setEditingAttribute(null)}
                className="px-4 py-2.5 text-[13px] text-[#666] hover:text-[#333] transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => handleUpdateAttribute(editingAttribute)}
                disabled={!editAttrName.trim() || saving}
                className="px-5 py-2.5 bg-[#222] text-white text-[13px] font-medium rounded-[8px] hover:bg-[#444] disabled:bg-[#e0e0e0] disabled:text-[#aaa] transition-colors"
              >
                저장
              </button>
            </div>
          </div>
        </OptionModal>
      )}
    </div>
  );
};

